import { useMutation } from '@tanstack/react-query'
import { apiClient, handleApiError } from '@/infrastructure/api/client'

export interface DataImportResult {
  investigationsCreated: number
  leadsCreated: number
  factsCreated: number
  errors: string[]
}

export interface ImportDataVariables {
  file: File
  clientId: string
}

const IMPORT_API_BASE = '/data-imports'
const EXPORT_API_BASE = '/data-exports'

/** Uploads a JSON import file. `clientId` correlates this upload with the
 * caller's already-open `data-imports/progress/:clientId` SSE channel -- the
 * import itself runs in the background on the server, so this request
 * resolves as soon as the file is validated and accepted (HTTP 202), not
 * once the import finishes; progress and the final result arrive over that
 * SSE channel instead. */
export function useImportData() {
  return useMutation({
    mutationFn: async ({ file, clientId }: ImportDataVariables): Promise<void> => {
      try {
        const formData = new FormData()
        formData.append('file', file)
        formData.append('clientId', clientId)

        // The shared apiClient sets a default 'application/json' Content-Type
        // header. Axios's transformRequest special-cases FormData when the
        // Content-Type looks like JSON: it JSON-stringifies the FormData's
        // entries instead of sending it as multipart -- since a File has no
        // enumerable properties, it serializes to an empty object, silently
        // dropping the file. Clearing the header lets axios/the browser set
        // the correct multipart Content-Type with boundary instead.
        await apiClient.post(IMPORT_API_BASE, formData, {
          headers: { 'Content-Type': undefined },
        })
      } catch (error) {
        throw handleApiError(error)
      }
    },
  })
}

export async function exportData(investigationIds?: string[]): Promise<Blob> {
  try {
    const params = new URLSearchParams()
    if (investigationIds && investigationIds.length > 0) {
      investigationIds.forEach((targetId) => params.append('investigationIds', targetId))
    }
    const response = await apiClient.get<Blob>(EXPORT_API_BASE, {
      params,
      responseType: 'blob',
    })
    return response.data
  } catch (error) {
    throw handleApiError(error)
  }
}
